import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Drawer,
  Switch,
  Select,
  MenuItem,
  InputBase,
  Button,
} from '@mui/material';
import AccessibilityNewIcon from '@mui/icons-material/AccessibilityNew';
import SearchIcon from '@mui/icons-material/Search';
import ReplayIcon from '@mui/icons-material/Replay';
import DescriptionIcon from '@mui/icons-material/Description';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

// Accessibility Profiles with detailed application and clearing methods
const accessibilityProfiles = [
  {
    name: 'Seizure Safe Profile',
    description: 'Clear flashes & reduces color',
    icon: '⚡',
    applyFunction: () => {
      // Reduce color intensity and slow down transitions
      document.body.classList.add('seizure-safe');
      const elementsToModify = document.querySelectorAll('body, body *');
      const safeElements = Array.from(elementsToModify).filter(el => 
        el.tagName !== 'SCRIPT' && 
        el.tagName !== 'STYLE' && 
        !el.closest('.accessibility-widget')
      );
      
      safeElements.forEach(el => {
        el.style.filter = 'saturate(0.5) brightness(0.9)';
        el.style.transition = 'all 0.5s ease';
      });
      
      // Disable rapid animations and flashing elements
      const flashyElements = document.querySelectorAll('.animated, video, gif');
      flashyElements.forEach(el => {
        el.style.filter = 'grayscale(100%)';
        el.style.opacity = '0.7';
      });
    },
    clearFunction: () => {
      document.body.classList.remove('seizure-safe');
      document.querySelectorAll('*').forEach(el => {
        el.style.filter = '';
        el.style.transition = '';
      });
      
      const flashyElements = document.querySelectorAll('.animated, video, gif');
      flashyElements.forEach(el => {
        el.style.filter = '';
        el.style.opacity = '';
      });
    }
  },
  {
    name: 'Vision Impaired Profile',
    description: 'Enhances website\'s visuals',
    icon: '👁️',
    applyFunction: () => {
      document.body.classList.add('vision-impaired');
      
      // Increase font size and contrast
      document.body.style.fontSize = '1.3em';
      document.body.style.lineHeight = '1.6';
      
      // High contrast color scheme
      document.querySelectorAll('*').forEach(el => {
        el.style.color = '#000';
        el.style.backgroundColor = '#fff';
        el.style.borderColor = '#000';
      });
      
      // Add text magnification
      const textElements = document.querySelectorAll('p, span, div, h1, h2, h3, h4, h5, h6');
      textElements.forEach(el => {
        el.style.textShadow = '0.5px 0.5px 1px rgba(0,0,0,0.3)';
      });
    },
    clearFunction: () => {
      document.body.classList.remove('vision-impaired');
      document.body.style.fontSize = '';
      document.body.style.lineHeight = '';
      
      document.querySelectorAll('*').forEach(el => {
        el.style.color = '';
        el.style.backgroundColor = '';
        el.style.borderColor = '';
        el.style.textShadow = '';
      });
    }
  },
  {
    name: 'ADHD Friendly Profile',
    description: 'More focus & fewer distractions',
    icon: '📄',
    applyFunction: () => {
      document.body.classList.add('adhd-friendly');
      
      // Pause animations and reduce visual noise
      document.querySelectorAll('*').forEach(el => {
        el.style.transition = 'none';
        el.style.animationPlayState = 'paused';
      });
      
      // Hide or minimize distracting elements
      const distractingElements = document.querySelectorAll('.animated, video, carousel, popup');
      distractingElements.forEach(el => {
        el.style.opacity = '0.5';
        el.style.filter = 'blur(2px)';
      });
      
      // Increase focus on main content
      const mainContent = document.querySelector('main, #main-content');
      if (mainContent) {
        mainContent.style.filter = 'contrast(1.2)';
      }
    },
    clearFunction: () => {
      document.body.classList.remove('adhd-friendly');
      
      document.querySelectorAll('*').forEach(el => {
        el.style.transition = '';
        el.style.animationPlayState = '';
      });
      
      const distractingElements = document.querySelectorAll('.animated, video, carousel, popup');
      distractingElements.forEach(el => {
        el.style.opacity = '';
        el.style.filter = '';
      });
      
      const mainContent = document.querySelector('main, #main-content');
      if (mainContent) {
        mainContent.style.filter = '';
      }
    }
  },
  {
    name: 'Cognitive Disability Profile',
    description: 'Assists with reading & focusing',
    icon: '👁️',
    applyFunction: () => {
      document.body.classList.add('cognitive-disability');
      
      // Simplify layout and increase readability
      document.querySelectorAll('p, div, section').forEach(el => {
        el.style.backgroundColor = '#f0f0f0';
        el.style.padding = '10px';
        el.style.borderRadius = '5px';
        el.style.marginBottom = '15px';
        el.style.lineHeight = '1.5';
      });
      
      // Highlight current reading section
      const headings = document.querySelectorAll('h1, h2, h3, h4, h5, h6');
      headings.forEach(el => {
        el.style.backgroundColor = '#e0e0e0';
        el.style.padding = '5px';
        el.style.borderLeft = '4px solid #2196f3';
      });
    },
    clearFunction: () => {
      document.body.classList.remove('cognitive-disability');
      
      document.querySelectorAll('p, div, section').forEach(el => {
        el.style.backgroundColor = '';
        el.style.padding = '';
        el.style.borderRadius = '';
        el.style.marginBottom = '';
        el.style.lineHeight = '';
      });
      
      const headings = document.querySelectorAll('h1, h2, h3, h4, h5, h6');
      headings.forEach(el => {
        el.style.backgroundColor = '';
        el.style.padding = '';
        el.style.borderLeft = '';
      });
    }
  },
  {
    name: 'Keyboard Navigation (Motor)',
    description: 'Use website with the keyboard',
    icon: '→',
    applyFunction: () => {
      document.body.classList.add('keyboard-nav');
      
      // Enhance keyboard navigation
      const focusableElements = document.querySelectorAll(
        'a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      
      focusableElements.forEach((el, index) => {
        el.setAttribute('tabindex', index + 1);
        el.style.outline = '3px solid #2196f3';
        el.style.outlineOffset = '2px';
      });
      
      // Add keyboard navigation hints
      const navHint = document.createElement('div');
      navHint.id = 'keyboard-nav-hint';
      navHint.style.position = 'fixed';
      navHint.style.bottom = '20px';
      navHint.style.right = '20px';
      navHint.style.backgroundColor = '#2196f3';
      navHint.style.color = 'white';
      navHint.style.padding = '10px';
      navHint.style.borderRadius = '5px';
      navHint.textContent = 'Use Tab to navigate, Enter to select';
      document.body.appendChild(navHint);
    },
    clearFunction: () => {
      document.body.classList.remove('keyboard-nav');
      
      const focusableElements = document.querySelectorAll(
        'a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      
      focusableElements.forEach(el => {
        el.removeAttribute('tabindex');
        el.style.outline = '';
        el.style.outlineOffset = '';
      });
      
      const navHint = document.getElementById('keyboard-nav-hint');
      if (navHint) {
        navHint.remove();
      }
    }
  },
  {
    name: 'Blind Users (Screen Reader)',
    description: 'Optimize website for screen-readers',
    icon: '🔊',
    applyFunction: () => {
      document.body.classList.add('screen-reader');
      
      // Enhance screen reader compatibility
      document.querySelectorAll('*').forEach(el => {
        // Add aria labels if not present
        if (!el.getAttribute('aria-label') && el.textContent) {
          el.setAttribute('aria-label', el.textContent.trim());
        }
        
        // Improve semantic structure
        el.setAttribute('role', 'region');
        el.style.cursor = 'pointer';
      });
      
      // Add audio cues with error handling
      try {
        const audioCue = new Audio('/assets/audio/screen-reader-cue.mp3');
        audioCue.onerror = () => {
          console.warn('Screen reader audio cue failed to load');
        };
        audioCue.play().catch(error => {
          console.warn('Failed to play screen reader audio cue:', error);
        });
      } catch (error) {
        console.warn('Error creating audio cue:', error);
      }
    },
    clearFunction: () => {
      document.body.classList.remove('screen-reader');
      
      document.querySelectorAll('*').forEach(el => {
        el.removeAttribute('aria-label');
        el.removeAttribute('role');
        el.style.cursor = '';
      });
    }
  }
];

const AccessibilityWidget = () => {
  const [open, setOpen] = useState(false);
  const [activeProfiles, setActiveProfiles] = useState({});
  const [language, setLanguage] = useState('ENGLISH');
  const [searchTerm, setSearchTerm] = useState('');

  // Load saved accessibility preferences on component mount
  useEffect(() => {
    try {
      const savedProfiles = localStorage.getItem('accessibilityProfiles');
      if (savedProfiles) {
        const parsedProfiles = JSON.parse(savedProfiles);
        setActiveProfiles(parsedProfiles);
        
        // Reapply active profiles
        Object.keys(parsedProfiles).forEach(profileName => {
          const profile = accessibilityProfiles.find(p => p.name === profileName);
          if (profile) {
            profile.applyFunction();
          }
        });
      }
    } catch (error) {
      console.warn('Error loading accessibility preferences:', error);
    }
  }, []);

  // Save active profiles when they change
  useEffect(() => {
    try {
      localStorage.setItem('accessibilityProfiles', JSON.stringify(activeProfiles));
    } catch (error) {
      console.warn('Error saving accessibility preferences:', error);
    }
  }, [activeProfiles]);

  const toggleDrawer = () => {
    setOpen(!open);
  };

  const toggleProfile = (profileName) => {
    setActiveProfiles(prev => {
      const newProfiles = { ...prev };
      const profile = accessibilityProfiles.find(p => p.name === profileName);
      
      if (!profile) {
        console.warn(`Profile not found: ${profileName}`);
        return prev;
      }
      
      try {
        if (newProfiles[profileName]) {
          // Deactivate profile
          profile.clearFunction();
          delete newProfiles[profileName];
        } else {
          // Activate profile
          profile.applyFunction();
          newProfiles[profileName] = true;
        }
      } catch (error) {
        console.error(`Error toggling profile ${profileName}:`, error);
      }
      
      return newProfiles;
    });
  };

  const resetSettings = () => {
    // Remove all active profiles
    Object.keys(activeProfiles).forEach(profileName => {
      const profile = accessibilityProfiles.find(p => p.name === profileName);
      profile.clearFunction();
    });
    setActiveProfiles({});
  };

  const openAccessibilityStatement = () => {
    window.open('/accessibility-statement', '_blank');
  };

  return (
    <>
      <IconButton
        onClick={toggleDrawer}
        sx={{
          position: 'fixed',
          bottom: 16,
          left: 16,
          zIndex: 1300,
          bgcolor: '#2196f3',
          color: 'white',
          '&:hover': {
            bgcolor: '#1976d2',
          },
        }}
      >
        <AccessibilityNewIcon />
      </IconButton>

      <Drawer
        anchor="right"
        open={open}
        onClose={toggleDrawer}
        sx={{
          '& .MuiDrawer-paper': {
            width: 400,
            backgroundColor: '#f5f5f5',
          },
        }}
      >
        <Box 
          sx={{ 
            bgcolor: '#2196f3', 
            color: 'white', 
            p: 2, 
            display: 'flex', 
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <Typography variant="h6">Accessibility Adjustments</Typography>
          <Select
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
            sx={{
              color: 'white',
              '& .MuiSelect-icon': {
                color: 'white',
              },
            }}
          >
            <MenuItem value="ENGLISH">ENGLISH</MenuItem>
            <MenuItem value="SPANISH">SPANISH</MenuItem>
            <MenuItem value="FRENCH">FRENCH</MenuItem>
          </Select>
        </Box>

        <Box sx={{ p: 2, display: 'flex', gap: 1 }}>
          <Button 
            variant="outlined" 
            startIcon={<ReplayIcon />} 
            onClick={resetSettings}
            sx={{ flex: 1 }}
          >
            Reset Settings
          </Button>
          <Button 
            variant="outlined" 
            startIcon={<DescriptionIcon />} 
            onClick={openAccessibilityStatement}
            sx={{ flex: 1 }}
          >
            Statement
          </Button>
          <Button 
            variant="outlined" 
            startIcon={<VisibilityOffIcon />} 
            onClick={toggleDrawer}
            sx={{ flex: 1 }}
          >
            Hide Interface
          </Button>
        </Box>

        <Box sx={{ p: 2 }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            bgcolor: 'white', 
            borderRadius: 1,
            mb: 2
          }}>
            <IconButton>
              <SearchIcon />
            </IconButton>
            <InputBase
              placeholder="Unclear content? Search in dictionary..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              fullWidth
            />
          </Box>

          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
            Choose the right accessibility profile for you
          </Typography>

          {accessibilityProfiles.map((profile) => (
            <Box 
              key={profile.name}
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                mb: 1,
                p: 1,
                bgcolor: activeProfiles[profile.name] ? '#e6f2ff' : 'transparent',
                borderRadius: 1
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography sx={{ mr: 2 }}>{profile.icon}</Typography>
                <Box>
                  <Typography variant="subtitle2">{profile.name}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {profile.description}
                  </Typography>
                </Box>
              </Box>
              <Switch
                checked={!!activeProfiles[profile.name]}
                onChange={() => toggleProfile(profile.name)}
              />
            </Box>
          ))}
        </Box>
      </Drawer>
    </>
  );
};

export default AccessibilityWidget; 